import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";

import { useUnifiedTeamData } from "../Hooks/useUnifiedTeamData";
import { useTournamentAuth } from "../../../utils/authorization";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  SoftButton,
} from "../../UI/DesignStandards";
import SquadSelect from "../ScoringToolbar/SquadSelect";
import { notify } from "../../UI/DesignStandards/Notifications/notify";
import { sanitizeOperationError } from "../../../utility/errorSanitization";
import {
  assignsStartingLane,
  autoAssignPlacements,
  buildPairs,
  copyLanePlacements,
  doublesPlacementSummary,
  emptySquadLaneForm,
  emptyTournamentLaneSettings,
  formatBlackoutsText,
  formatPair,
  laneStatesFromPayload,
  movementSummary,
  nextOpenSlot,
  parseBlackoutsText,
  pairStartFromLane,
  slotCountForBoard,
  squadLaneFormFromState,
  teamLabel,
  teamsOnLane,
  teamsOnPair,
} from "../../../utility/laneAssignment";
import { useTournamentId } from "../../../context/TournamentRouteContext";
import {
  fetchSquadLaneState,
  fetchSquadLaneStates,
  fetchTournamentLaneSettings,
  isLaneSlotTakenError,
  saveSquadLaneAssignments,
  saveSquadLaneRange,
} from "../../../utility/laneAssignmentApi";

const occupantClass = (isSelected) =>
  `w-full rounded-lg border min-h-14 flex items-stretch ${
    isSelected
      ? "border-brand-gold-ink bg-brand-gold-ink/10"
      : "border-brand-charcoal/20 bg-white"
  }`;

const OccupantButton = ({ team, selected, onSelect, onRemove, canRemove }) => (
  <div className={occupantClass(selected)}>
    <button
      type="button"
      onClick={() => onSelect(team.id)}
      className="flex-1 text-left px-3 py-2 min-w-0"
    >
      <div className="text-sm font-display font-bold text-brand-charcoal truncate">
        {teamLabel(team)}
      </div>
    </button>
    {canRemove ? (
      <button
        type="button"
        aria-label={`Remove ${teamLabel(team)}`}
        title="Remove from this pair"
        onClick={() => onRemove(team.id)}
        className="shrink-0 px-2 text-brand-muted hover:text-red-700"
      >
        <FaTimes className="w-3 h-3" aria-hidden="true" />
      </button>
    ) : null}
  </div>
);

const OpenLaneButton = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left rounded-lg border border-dashed border-brand-charcoal/25 bg-brand-cream px-3 py-2 min-h-14"
  >
    <div className="text-sm font-display font-bold text-brand-charcoal">
      {label}
    </div>
  </button>
);

const mergeLaneForms = (prev, incoming, dirtyIds) => {
  const next = { ...prev };
  Object.entries(incoming || {}).forEach(([squadId, state]) => {
    if (dirtyIds.has(squadId)) return;
    next[squadId] = squadLaneFormFromState(state);
  });
  return next;
};

const LaneAssignmentWorkspace = () => {
  const tournamentId = useTournamentId();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canManage } = useTournamentAuth(tournamentId);
  const { data, loading, teams, refetch } = useUnifiedTeamData(tournamentId, {
    squad: "all",
    event: "all",
    status: "all",
    date: "all",
    search: "",
  });

  const squads = data?.squads || [];
  const events = data?.events || [];
  const squadFromUrl = searchParams.get("squad") || "";
  const selectedSquadId =
    squadFromUrl && squads.some((squad) => squad.id === squadFromUrl)
      ? squadFromUrl
      : "";

  const [tournamentSettings, setTournamentSettings] = useState(
    emptyTournamentLaneSettings()
  );
  const [laneBySquad, setLaneBySquad] = useState({});
  const [dataSeen, setDataSeen] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [saving, setSaving] = useState(false);
  const dirtySquadsRef = useRef(new Set());

  if (data !== dataSeen) {
    setDataSeen(data);
    if (data) {
      setLaneBySquad((prev) =>
        mergeLaneForms(prev, laneStatesFromPayload(data), dirtySquadsRef.current)
      );
    }
  }

  const currentLane = selectedSquadId
    ? laneBySquad[selectedSquadId] || emptySquadLaneForm()
    : emptySquadLaneForm();
  const startLane = currentLane.startLane;
  const endLane = currentLane.endLane;
  const blackoutsText = currentLane.blackoutsText;
  const placements = currentLane.placements;

  const markDirty = () => {
    if (selectedSquadId) dirtySquadsRef.current.add(selectedSquadId);
  };

  const patchLane = (updater) => {
    if (!selectedSquadId) return;
    markDirty();
    setLaneBySquad((prev) => {
      const previous = prev[selectedSquadId] || emptySquadLaneForm();
      const nextSlice =
        typeof updater === "function"
          ? updater(previous)
          : { ...previous, ...updater };
      return { ...prev, [selectedSquadId]: nextSlice };
    });
  };

  useEffect(() => {
    let cancelled = false;
    if (!tournamentId) return undefined;
    fetchTournamentLaneSettings(tournamentId)
      .then((next) => {
        if (!cancelled) setTournamentSettings(next);
      })
      .catch(() => {
        if (!cancelled) setTournamentSettings(emptyTournamentLaneSettings());
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  const squadIdsKey = squads.map((squad) => squad.id).join(",");

  useEffect(() => {
    if (!squadIdsKey) return undefined;
    const ids = squadIdsKey.split(",").filter(Boolean);
    let cancelled = false;
    fetchSquadLaneStates(ids)
      .then((bySquad) => {
        if (cancelled) return;
        setLaneBySquad((prev) =>
          mergeLaneForms(prev, bySquad, dirtySquadsRef.current)
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [squadIdsKey]);

  const squadTeams = useMemo(
    () => (teams || []).filter((team) => team.squadID === selectedSquadId),
    [teams, selectedSquadId]
  );

  const squadEvents = useMemo(() => {
    const ids = new Set(
      squadTeams
        .map((team) => team.eventsID)
        .filter(Boolean)
    );
    return (events || []).filter(
      (event) => event.isPrimary && ids.has(event.id)
    );
  }, [squadTeams, events]);

  const eventFromUrl = searchParams.get("event") || "";
  const selectedSquad = squads.find((squad) => squad.id === selectedSquadId);
  const selectedEventId =
    eventFromUrl && squadEvents.some((event) => event.id === eventFromUrl)
      ? eventFromUrl
      : squadEvents.find((event) =>
          (selectedSquad?.eventIds || []).includes(event.id)
        )
          ?.id ||
        squadEvents[0]?.id ||
        "";

  useEffect(() => {
    setSelectedTeamId(null);
  }, [selectedSquadId, selectedEventId]);

  const eventTeams = useMemo(
    () =>
      selectedEventId
        ? squadTeams.filter((team) => team.eventsID === selectedEventId)
        : squadTeams,
    [squadTeams, selectedEventId]
  );

  const eventPlacements = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(placements).filter(([teamId]) =>
          eventTeams.some((team) => team.id === teamId)
        )
      ),
    [placements, eventTeams]
  );

  const bowlerQuantity = useMemo(() => {
    const eventId =
      selectedEventId ||
      eventTeams[0]?.eventsID ||
      selectedSquad?.eventIds?.[0];
    const event = events.find((item) => item.id === eventId);
    return event?.bowlerQuantity || 2;
  }, [
    selectedEventId,
    eventTeams,
    selectedSquad,
    events,
  ]);

  const pairs = buildPairs(
    startLane,
    endLane,
    parseBlackoutsText(blackoutsText)
  );
  const openPairs = pairs.filter((pair) => !pair.blocked);
  const fullestPair = openPairs.reduce(
    (max, pair) =>
      Math.max(max, teamsOnPair(eventPlacements, pair.pairStart).length),
    0
  );
  const slotCount = slotCountForBoard(
    eventTeams.length,
    openPairs.length,
    fullestPair
  );

  const placedCount = eventTeams.filter((team) => placements[team.id]).length;
  const unassigned = eventTeams.filter((team) => !placements[team.id]);

  const handleSelectSquad = (squadId) => {
    const next = new URLSearchParams(searchParams);
    if (squadId) next.set("squad", squadId);
    else next.delete("squad");
    next.delete("event");
    setSearchParams(next, { replace: true });
  };

  const handleSelectEvent = (eventId) => {
    const next = new URLSearchParams(searchParams);
    if (eventId) next.set("event", eventId);
    else next.delete("event");
    setSearchParams(next, { replace: true });
    setSelectedTeamId(null);
  };

  const assignLanes = assignsStartingLane(
    tournamentSettings,
    bowlerQuantity
  );

  const reloadSquadLaneState = useCallback(async () => {
    if (!selectedSquadId) return;
    const state = await fetchSquadLaneState(selectedSquadId);
    if (dirtySquadsRef.current.has(selectedSquadId)) return;
    setLaneBySquad((prev) => ({
      ...prev,
      [selectedSquadId]: squadLaneFormFromState(state),
    }));
  }, [selectedSquadId]);

  const placeTeam = (teamId, pairStart, slot, startingLane) => {
    patchLane((prev) => ({
      ...prev,
      placements: {
        ...prev.placements,
        [teamId]: {
          pairStart,
          slot,
          startingLane: startingLane || null,
        },
      },
    }));
    setSelectedTeamId(null);
  };

  const handlePlace = (pairStart, startingLane = null) => {
    if (!canManage || !selectedTeamId) return;
    const current = placements[selectedTeamId];
    if (
      current?.pairStart === pairStart &&
      (current?.startingLane || null) === (startingLane || null)
    ) {
      setSelectedTeamId(null);
      return;
    }
    const slot = nextOpenSlot(eventPlacements, pairStart, slotCount);
    if (!slot) {
      notify.error("This pair is full.");
      return;
    }
    placeTeam(selectedTeamId, pairStart, slot, startingLane);
  };

  const handleAutoAssign = () => {
    if (!openPairs.length) {
      notify.error("Enter start and end lanes before assigning.");
      return;
    }
    const eventIds = new Set(eventTeams.map((team) => team.id));
    patchLane((prev) => {
      const kept = Object.fromEntries(
        Object.entries(prev.placements || {}).filter(
          ([teamId]) => !eventIds.has(teamId)
        )
      );
      return {
        ...prev,
        placements: {
          ...kept,
          ...autoAssignPlacements({
            teams: eventTeams,
            pairs,
            assignLanes,
          }),
        },
      };
    });
    setSelectedTeamId(null);
  };

  const handleClearPlacements = () => {
    const eventIds = new Set(eventTeams.map((team) => team.id));
    patchLane((prev) => ({
      ...prev,
      placements: Object.fromEntries(
        Object.entries(prev.placements || {}).filter(
          ([teamId]) => !eventIds.has(teamId)
        )
      ),
    }));
    setSelectedTeamId(null);
  };

  const handleCopyStartingLanes = (sourceEventId) => {
    if (!canManage || !sourceEventId || !selectedEventId) return;
    if (!openPairs.length) {
      notify.error("Enter start and end lanes before assigning.");
      return;
    }
    const sourceTeams = squadTeams.filter(
      (team) => team.eventsID === sourceEventId
    );
    const copied = copyLanePlacements({
      sourceTeams,
      targetTeams: eventTeams,
      placements,
      doublesPlacement: tournamentSettings.doublesPlacement,
      slotCount,
      openPairStarts: openPairs.map((pair) => pair.pairStart),
    });
    const placedBefore = eventTeams.filter((team) => placements[team.id])
      .length;
    const placedAfter = eventTeams.filter((team) => copied[team.id]).length;
    const added = placedAfter - placedBefore;
    const stillOpen = eventTeams.length - placedAfter;
    const sourceName =
      events.find((event) => event.id === sourceEventId)?.eventName ||
      "the other event";
    if (added < 1) {
      notify.error(
        `No unassigned teams matched ${sourceName} starting lanes on the open pairs.`
      );
      return;
    }
    patchLane((prev) => ({ ...prev, placements: copied }));
    setSelectedTeamId(null);
    notify.success(
      stillOpen
        ? `Copied starting lanes from ${sourceName}. ${stillOpen} still unassigned.`
        : `Copied starting lanes from ${sourceName}.`
    );
  };

  const handleRemoveTeam = (teamId) => {
    if (!canManage || !teamId) return;
    patchLane((prev) => {
      const next = { ...prev.placements };
      delete next[teamId];
      return { ...prev, placements: next };
    });
    setSelectedTeamId((current) => (current === teamId ? null : current));
  };

  const handleBlackoutPair = (pairStart) => {
    if (!canManage || pairStart == null) return;
    patchLane((prev) => {
      const current = parseBlackoutsText(prev.blackoutsText);
      const nextPlacements = { ...prev.placements };
      Object.entries(nextPlacements).forEach(([teamId, place]) => {
        if (place?.pairStart === pairStart) delete nextPlacements[teamId];
      });
      return {
        ...prev,
        blackoutsText: current.includes(pairStart)
          ? prev.blackoutsText
          : formatBlackoutsText([...current, pairStart]),
        placements: nextPlacements,
      };
    });
    setSelectedTeamId((currentId) =>
      currentId && placements[currentId]?.pairStart === pairStart
        ? null
        : currentId
    );
  };

  const handleSave = async () => {
    if (!selectedSquadId || !canManage) return;
    const start = pairStartFromLane(startLane);
    const end = pairStartFromLane(endLane);
    if ((start && !end) || (!start && end)) {
      notify.error("Enter both start and end lanes.");
      return;
    }
    if (start && end && end < start) {
      notify.error("End lane must be the same as start lane or after it.");
      return;
    }
    setSaving(true);
    try {
      await saveSquadLaneRange(selectedSquadId, {
        startLane: start,
        endLane: end,
        blackouts: parseBlackoutsText(blackoutsText),
      });
      await saveSquadLaneAssignments({
        squadId: selectedSquadId,
        teams: squadTeams,
        placements,
      });
      dirtySquadsRef.current.delete(selectedSquadId);
      await refetch();
      await reloadSquadLaneState();
      notify.success("Lane assignments saved.");
    } catch (error) {
      if (isLaneSlotTakenError(error)) {
        notify.error(
          "That pair already has a team in that place. Try again."
        );
        dirtySquadsRef.current.delete(selectedSquadId);
        await refetch();
        await reloadSquadLaneState();
      } else {
        notify.error(sanitizeOperationError.save(error, "lane assignments"));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="card-board p-6 text-sm text-brand-muted">
        Loading teams…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card-board p-4 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SquadSelect
            label="Squad"
            squads={squads}
            value={selectedSquadId}
            onChange={handleSelectSquad}
            className="mb-0"
          />
          {squadEvents.length > 1 ? (
            <FormField
              label="Event"
              type="select"
              value={selectedEventId}
              onChange={(e) => handleSelectEvent(e.target.value)}
              className="mb-0"
              disabled={!selectedSquadId}
            >
              {squadEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.eventName}
                </option>
              ))}
            </FormField>
          ) : null}
          <FormField
            label="Start lane"
            type="number"
            min="1"
            value={startLane}
            onChange={(e) => patchLane({ startLane: e.target.value })}
            className="mb-0"
            disabled={!selectedSquadId || !canManage || saving}
          />
          <FormField
            label="End lane"
            type="number"
            min="1"
            value={endLane}
            onChange={(e) => patchLane({ endLane: e.target.value })}
            className="mb-0"
            disabled={!selectedSquadId || !canManage || saving}
          />
          <FormField
            label="Unused pairs"
            type="text"
            value={blackoutsText}
            onChange={(e) => patchLane({ blackoutsText: e.target.value })}
            placeholder="15-16, 21-22"
            className="mb-0"
            disabled={!selectedSquadId || !canManage || saving}
          />
        </div>

        <div className="text-sm text-brand-muted space-y-1">
          <div>{movementSummary(tournamentSettings)}</div>
          {bowlerQuantity === 2 ? (
            <div>{doublesPlacementSummary(tournamentSettings)}</div>
          ) : null}
        </div>

        {selectedSquadId && canManage ? (
          <div className="flex flex-wrap gap-2">
            <PrimaryButton
              type="button"
              onClick={handleAutoAssign}
              disabled={saving}
            >
              Auto-assign
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={handleClearPlacements}
              disabled={saving}
            >
              Clear places
            </SecondaryButton>
            {squadEvents
              .filter((event) => event.id !== selectedEventId)
              .filter((event) =>
                squadTeams.some(
                  (team) =>
                    team.eventsID === event.id && placements[team.id]
                )
              )
              .map((event) => (
                <SecondaryButton
                  key={event.id}
                  type="button"
                  onClick={() => handleCopyStartingLanes(event.id)}
                  disabled={saving || unassigned.length === 0}
                >
                  Copy starting lanes from {event.eventName}
                </SecondaryButton>
              ))}
            <PrimaryButton type="button" onClick={handleSave} loading={saving}>
              Save
            </PrimaryButton>
          </div>
        ) : null}

        {selectedSquadId ? (
          <p className="text-sm text-brand-charcoal">
            {placedCount} of {eventTeams.length} teams placed.
            {selectedTeamId
              ? assignLanes
                ? " Click a lane to place the selected team."
                : " Click a pair to place the selected team."
              : assignLanes
                ? " Click a team, then click a lane."
                : " Click a team, then click a pair."}
          </p>
        ) : null}
      </div>

      {!selectedSquadId ? (
        <div className="card-board p-8 text-center text-brand-muted">
          Pick a squad to assign lanes.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="card-board p-4">
            <h2 className="heading-display text-lg mb-3">Unassigned</h2>
            {unassigned.length === 0 ? (
              <p className="text-sm text-brand-muted">Every team has a place.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {unassigned.map((team) => (
                  <button
                    key={team.id}
                    type="button"
                    onClick={() =>
                      setSelectedTeamId((current) =>
                        current === team.id ? null : team.id
                      )
                    }
                    className={`px-3 py-2 rounded-lg text-sm font-display font-bold border ${
                      selectedTeamId === team.id
                        ? "border-brand-gold-ink bg-brand-gold-ink/10 text-brand-charcoal"
                        : "border-brand-charcoal/20 text-brand-charcoal hover:bg-brand-charcoal/5"
                    }`}
                  >
                    {teamLabel(team)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-x-auto pb-2">
            <div className="flex gap-3 min-w-min">
              {openPairs.length === 0 ? (
                <div className="card-board p-8 text-sm text-brand-muted">
                  Enter start and end lanes to see the pairs.
                </div>
              ) : (
                openPairs.map((pair) => {
                  const occupants = teamsOnPair(eventPlacements, pair.pairStart)
                    .map(([teamId]) =>
                      eventTeams.find((team) => team.id === teamId)
                    )
                    .filter(Boolean);
                  const squadOccupants = teamsOnPair(
                    placements,
                    pair.pairStart
                  );
                  const openCount = Math.max(0, slotCount - occupants.length);
                  const toggleTeam = (teamId) =>
                    setSelectedTeamId((current) =>
                      current === teamId ? null : teamId
                    );
                  const leftLane = pair.pairStart;
                  const rightLane = pair.pairStart + 1;
                  const leftOccupants = teamsOnLane(
                    eventPlacements,
                    pair.pairStart,
                    leftLane
                  )
                    .map(([teamId]) =>
                      eventTeams.find((team) => team.id === teamId)
                    )
                    .filter(Boolean);
                  const rightOccupants = teamsOnLane(
                    eventPlacements,
                    pair.pairStart,
                    rightLane
                  )
                    .map(([teamId]) =>
                      eventTeams.find((team) => team.id === teamId)
                    )
                    .filter(Boolean);
                  const unsetOccupants = occupants.filter((team) => {
                    const lane = placements[team.id]?.startingLane;
                    return lane !== leftLane && lane !== rightLane;
                  });
                  return (
                    <div
                      key={pair.pairStart}
                      className={`card-board p-3 shrink-0 ${
                        assignLanes ? "w-64" : "w-56"
                      }`}
                    >
                      <div className="w-full font-display font-bold text-brand-charcoal mb-1 text-center">
                        {pair.label}
                      </div>
                      <div className="text-xs text-brand-muted text-center mb-2">
                        {occupants.length} of {slotCount}
                      </div>
                      {canManage && squadOccupants.length === 0 ? (
                        <SoftButton
                          type="button"
                          size="xs"
                          color="cream"
                          className="w-full mb-2"
                          disabled={saving}
                          onClick={() => handleBlackoutPair(pair.pairStart)}
                        >
                          Unused
                        </SoftButton>
                      ) : null}
                      {assignLanes ? (
                        <div className="space-y-2">
                          {unsetOccupants.map((occupant) => (
                            <OccupantButton
                              key={occupant.id}
                              team={occupant}
                              selected={occupant.id === selectedTeamId}
                              onSelect={toggleTeam}
                              onRemove={handleRemoveTeam}
                              canRemove={canManage && !saving}
                            />
                          ))}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                              {leftOccupants.map((occupant) => (
                                <OccupantButton
                                  key={occupant.id}
                                  team={occupant}
                                  selected={occupant.id === selectedTeamId}
                                  onSelect={toggleTeam}
                                  onRemove={handleRemoveTeam}
                                  canRemove={canManage && !saving}
                                />
                              ))}
                              {openCount > 0 ? (
                                <OpenLaneButton
                                  label={String(leftLane)}
                                  onClick={() =>
                                    handlePlace(pair.pairStart, leftLane)
                                  }
                                />
                              ) : null}
                            </div>
                            <div className="space-y-2">
                              {rightOccupants.map((occupant) => (
                                <OccupantButton
                                  key={occupant.id}
                                  team={occupant}
                                  selected={occupant.id === selectedTeamId}
                                  onSelect={toggleTeam}
                                  onRemove={handleRemoveTeam}
                                  canRemove={canManage && !saving}
                                />
                              ))}
                              {openCount > 0 ? (
                                <OpenLaneButton
                                  label={String(rightLane)}
                                  onClick={() =>
                                    handlePlace(pair.pairStart, rightLane)
                                  }
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {occupants.map((occupant) => (
                            <OccupantButton
                              key={occupant.id}
                              team={occupant}
                              selected={occupant.id === selectedTeamId}
                              onSelect={toggleTeam}
                              onRemove={handleRemoveTeam}
                              canRemove={canManage && !saving}
                            />
                          ))}
                          {openCount > 0 ? (
                            <OpenLaneButton
                              label={
                                openCount === 1 ? "Open" : `${openCount} open`
                              }
                              onClick={() => handlePlace(pair.pairStart)}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {pairs.some((pair) => pair.blocked) ? (
            <p className="text-sm text-brand-muted">
              Unused pairs:{" "}
              {pairs
                .filter((pair) => pair.blocked)
                .map((pair) => pair.label)
                .join(", ")}
            </p>
          ) : null}

          {selectedTeamId && placements[selectedTeamId] ? (
            <SecondaryButton
              type="button"
              onClick={() => handleRemoveTeam(selectedTeamId)}
            >
              Remove {teamLabel(eventTeams.find((t) => t.id === selectedTeamId))}{" "}
              from{" "}
              {placements[selectedTeamId].startingLane
                ? `lane ${placements[selectedTeamId].startingLane}`
                : formatPair(placements[selectedTeamId].pairStart)}
            </SecondaryButton>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default LaneAssignmentWorkspace;
